import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Row, Typography, Button } from "antd";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import moment from "moment";

import BackButton from "components/BackButton";
import {
  TextInput,
  ImageUpload,
  DatePicker,
  TextEditor,
  Select,
} from "components/common";
import { createContestRequest, updateVotingLogicRequest } from "providers/ContestProvider/slice";
import Helper from "utils/helpers";
import "./styles.less";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Tên cuộc thi là bắt buộc"),
  bannerImage: Yup.object().nullable().required("Banner là bắt buộc"),
  bannerContestSquare: Yup.object().nullable(),
  startDate: Yup.date().required("Ngày bắt đầu là bắt buộc"),
  endDate: Yup.date()
    .min(Yup.ref("startDate"), "Ngày kết thúc phải sau ngày bắt đầu")
    .required("Ngày kết thúc là bắt buộc"),
  description: Yup.string().required("Mô tả là bắt buộc"),
  rules: Yup.string().required("Thể lệ là bắt buộc"),
  routeBanner: Yup.object().nullable(),
  logoContest: Yup.object().nullable(),
  timelineContest: Yup.object().nullable(),
  registerFormLink: Yup.string().url("URL không hợp lệ").nullable(),
  votingLogic: Yup.number().oneOf([1, 2], "Logic bỏ phiếu không hợp lệ").nullable(),
});

const ContestCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formikRef = useRef();

  const isLoading = useSelector((state) => state.contestSlice.isLoading);

  const handleCreateContest = (values) => {
    const contestData = {
      name: values.name,
      bannerImage: values.bannerImage?.url || values.bannerImage,
      bannerContestSquare: values.bannerContestSquare?.url || values.bannerContestSquare || null,
      startDate: moment(values.startDate).toISOString(),
      endDate: moment(values.endDate).toISOString(),
      description: values.description,
      rules: values.rules,
      routeBanner: values.routeBanner?.url || values.routeBanner || null,
      logoContest: values.logoContest?.url || values.logoContest || null,
      timelineContest: values.timelineContest?.url || values.timelineContest || null,
      registerFormLink: values.registerFormLink || null,
      votingLogic: values.votingLogic || 1,
      isActive: true,
    };

    dispatch(createContestRequest(contestData))
      .then(async (result) => {
        // If contest was created successfully and has an ID, update voting logic
        if (result?.payload?.data?._id && values.votingLogic && values.votingLogic !== 1) {
          try {
            await dispatch(updateVotingLogicRequest({
              contestId: result.payload.data._id,
              votingLogic: values.votingLogic
            }));
          } catch (error) {
            console.error("Update voting logic error:", error);
            // Contest was created but voting logic update failed
            Helper.toastr(
              "Cảnh báo",
              "Cuộc thi đã được tạo nhưng không thể cập nhật logic bỏ phiếu",
              "warning"
            );
          }
        }
        
        Helper.toastr(
          "Tạo cuộc thi thành công",
          "Cuộc thi đã được tạo thành công",
          "success"
        );

        // Navigate back to contest list
        navigate("/voting/contest_list", {
          state: { canGoBack: true },
          replace: true,
        });
      })
      .catch((error) => {
        console.error("Create contest error:", error);
        Helper.toastr(
          "Tạo cuộc thi thất bại",
          "Có lỗi xảy ra khi tạo cuộc thi. Vui lòng thử lại.",
          "error"
        );
      });
  };

  const initialValues = {
    name: "",
    bannerImage: null,
    bannerContestSquare: null,
    startDate: null,
    endDate: null,
    description: "",
    rules: "",
    routeBanner: null,
    logoContest: null,
    timelineContest: null,
    registerFormLink: "",
    votingLogic: 1,
  };

  return (
    <div className="contest-create-wrapper">
      <Row className="contest-create-header" justify="start" align="middle">
        <BackButton />
        <Typography.Title level={4}>Tạo cuộc thi</Typography.Title>
      </Row>
      <div className="create-wrapper">
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleCreateContest}
        >
          {({ handleSubmit: formikHandleSubmit, values, setFieldValue }) => (
            <Form>
              <div className="contest-form-container">
                {/* Banner Image */}
                <div className="form-section">
                  <div className="section-title">Banner cuộc thi</div>
                  <div className="upload-note">
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Banner tỉ lệ 3 ngang 1 dọc (hình chữ nhật)
                    </small>
                  </div>
                  <ImageUpload
                    name="bannerImage"
                    setFieldValue={setFieldValue}
                    required
                    className="contest-banner-upload"
                    aspectRatio={3}
                    disableCrop={true}
                  />
                </div>

                {/* Square Banner Image */}
                <div className="form-section">
                  <div className="section-title">Banner vuông cuộc thi</div>
                  <div className="upload-note">
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Banner tỷ lệ 1:1 (vuông), có thể cắt ảnh để phù hợp
                    </small>
                  </div>
                  <ImageUpload
                    name="bannerContestSquare"
                    setFieldValue={setFieldValue}
                    className="contest-square-banner-upload"
                    aspectRatio={1}
                    cropModalProps={{ width: 600 }}
                  />
                </div>

                {/* Basic Information */}
                <div className="form-section">
                  <div className="section-title">Thông tin cơ bản</div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Tên cuộc thi:</div>
                      <TextInput name="name" size="large" required />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Ngày bắt đầu:</div>
                      <DatePicker
                        name="startDate"
                        size="large"
                        showTime={false}
                        format="DD/MM/YYYY"
                        placeholder="Chọn ngày bắt đầu"
                        required
                      />
                    </div>
                    <div className="form-item">
                      <div className="item-title">Ngày kết thúc:</div>
                      <DatePicker
                        name="endDate"
                        size="large"
                        showTime={false}
                        format="DD/MM/YYYY"
                        placeholder="Chọn ngày kết thúc"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Logic bỏ phiếu:</div>
                      <Select
                        name="votingLogic"
                        size="large"
                        placeholder="Chọn logic bỏ phiếu"
                        options={[
                          { value: 1, label: "Bỏ phiếu hàng ngày (người dùng có thể bỏ phiếu mỗi ngày một lần cho mỗi ứng viên)" },
                          { value: 2, label: "Bỏ phiếu một lần (người dùng chỉ có thể bỏ phiếu một lần cho mỗi ứng viên trong suốt cuộc thi)" }
                        ]}
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Images and Links */}
                <div className="form-section">
                  <div className="section-title">Hình ảnh và liên kết bổ sung</div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Banner lộ trình:</div>
                      <div className="upload-note-small">
                        <small style={{ color: '#666', fontSize: '11px' }}>
                        Banner tỉ lệ 3 ngang 1 dọc (hình chữ nhật)
                        </small>
                      </div>
                      <ImageUpload
                        name="routeBanner"
                        setFieldValue={setFieldValue}
                        className="contest-additional-upload"
                        aspectRatio={3}
                        disableCrop={true}
                      />
                    </div>
                    <div className="form-item">
                      <div className="item-title">Logo cuộc thi:</div>
                      <div className="upload-note-small">
                        <small style={{ color: '#666', fontSize: '11px' }}>
                        Banner tỉ lệ 1:1 (vuông)
                        </small>
                      </div>
                      <ImageUpload
                        name="logoContest"
                        setFieldValue={setFieldValue}
                        className="contest-additional-upload"
                        aspectRatio={1}
                        disableCrop={true}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Timeline cuộc thi:</div>
                      <div className="upload-note-small">
                        <small style={{ color: '#666', fontSize: '11px' }}>
                        Banner tỉ lệ 3 ngang 1 dọc (hình chữ nhật)
                        </small>
                      </div>
                      <ImageUpload
                        name="timelineContest"
                        setFieldValue={setFieldValue}
                        className="contest-additional-upload"
                        aspectRatio={3}
                        disableCrop={true}
                      />
                    </div>
                    <div className="form-item">
                      <div className="item-title">Link form đăng ký:</div>
                      <TextInput 
                        name="registerFormLink" 
                        size="large" 
                        placeholder="https://forms.google.com/register"
                      />
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="form-section">
                  <div className="section-title">Mô tả</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Mô tả cuộc thi:</div>
                      <TextEditor
                        name="description"
                        placeholder="Nhập mô tả chi tiết về cuộc thi..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Rules */}
                <div className="form-section">
                  <div className="section-title">Thể lệ</div>
                  <div className="form-row">
                    <div className="form-item">
                      <div className="item-title">Thể lệ cuộc thi:</div>
                      <TextEditor
                        name="rules"
                        placeholder="Nhập thể lệ cuộc thi..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="form-actions">
                  <Button
                    loading={isLoading}
                    size="large"
                    type="primary"
                    onClick={() => formikRef.current?.submitForm()}
                  >
                    Tạo cuộc thi
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ContestCreate;
